import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import CegjelzoSecurityNotice from "./CegjelzoSecurityNotice.jsx";
import classes from "./cegjelzoSearch.module.scss";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as cegjelzoApi from "../../utils/cegjelzo/cegjelzoApi.js";
import {
  buildShowMoreOption,
  CEGJELZO_AUTOCOMPLETE_PAGE_SIZE,
  isShowMoreOption,
  isTaxNumberLikeQuery,
  mapSearchResponseToAutocompleteResults,
  normalizeCegjelzoNameSearchQuery,
  normalizeTaxNumberForCegjelzoSearch,
  shouldShowTaxMinDigitsHint,
} from "../../utils/cegjelzo/cegjelzoLookup.js";
import { normalizeCegjelzoRegistryType } from "../../utils/cegjelzo/mapCegjelzoCompanyData.js";
import i18n from "../../../../utils/i18n";

const normalizeCompanyType = (companyData) => ({
  ...companyData,
  type: normalizeCegjelzoRegistryType(companyData?.type),
});

/**
 * CegjelzoSearch - Company search autocomplete using the Cégjelző API.
 *
 * Props:
 *   onCompanySelected(fullCompanyData) - called when user confirms a company
 *   onManualFill() - called when user wants to fill manually instead
 */
const CegjelzoSearch = ({ onCompanySelected, onManualFill, onSearchFocusChange }) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const [autocompletePage, setAutocompletePage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeAutocompleteQuery, setActiveAutocompleteQuery] = useState("");

  const showMoreClickRef = useRef(false);
  const listboxRef = useRef(null);
  const listboxScrollLockRef = useRef({ active: false, scrollTop: 0 });
  const latestSearchQueryRef = useRef("");
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const taxNumberLikeInput = isTaxNumberLikeQuery(inputValue);

  const resetAutocompletePagination = useCallback(() => {
    setAutocompletePage(1);
    setHasMoreResults(false);
    setActiveAutocompleteQuery("");
  }, []);

  const applyListboxScrollLock = useCallback(() => {
    const lock = listboxScrollLockRef.current;
    if (!lock.active || !listboxRef.current) return;
    listboxRef.current.scrollTop = lock.scrollTop;
  }, []);

  const optionsWithShowMoreLength = useMemo(() => {
    if (!hasMoreResults || taxNumberLikeInput || autocompleteResults.length === 0) {
      return autocompleteResults.length;
    }
    return autocompleteResults.length + 1;
  }, [autocompleteResults.length, hasMoreResults, taxNumberLikeInput]);

  useLayoutEffect(() => {
    applyListboxScrollLock();
  }, [applyListboxScrollLock, autocompleteResults, loadingMore, optionsWithShowMoreLength]);

  useEffect(() => {
    applyListboxScrollLock();
    if (!loadingMore && listboxScrollLockRef.current.active) {
      listboxScrollLockRef.current.active = false;
    }
  }, [applyListboxScrollLock, autocompleteResults, loadingMore, optionsWithShowMoreLength]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query) => {
        const trimmed = query.trim();
        const taxLike = isTaxNumberLikeQuery(trimmed);
        const autocompleteQuery = taxLike
          ? trimmed
          : normalizeCegjelzoNameSearchQuery(trimmed);

        try {
          if (taxLike) {
            if (shouldShowTaxMinDigitsHint(trimmed)) {
              if (isMounted.current) {
                setAutocompleteResults([]);
                setHasSearched(false);
                setOptionsOpen(false);
                resetAutocompletePagination();
              }
              return;
            }
          } else if (trimmed.length < 3) {
            if (isMounted.current) {
              setAutocompleteResults([]);
              setHasSearched(false);
              setOptionsOpen(false);
              resetAutocompletePagination();
            }
            return;
          }

          if (isMounted.current) {
            setHasSearched(true);
          }

          if (taxLike) {
            const taxValue = normalizeTaxNumberForCegjelzoSearch(trimmed);
            if (!taxValue) {
              if (isMounted.current) {
                setAutocompleteResults([]);
                resetAutocompletePagination();
              }
              return;
            }
            const searchResponse = await cegjelzoApi.searchByTaxNumber(taxValue);
            const data = mapSearchResponseToAutocompleteResults(searchResponse);
            if (isMounted.current) {
              setAutocompleteResults(data);
              setOptionsOpen(data.length > 0);
              resetAutocompletePagination();
            }
          } else {
            const response = await cegjelzoApi.autocomplete(autocompleteQuery, {
              page: 1,
              limit: CEGJELZO_AUTOCOMPLETE_PAGE_SIZE,
            });
            const data = response.result;
            if (isMounted.current) {
              setAutocompleteResults(data);
              setOptionsOpen(data.length > 0);
              setAutocompletePage(1);
              setHasMoreResults(data.length >= CEGJELZO_AUTOCOMPLETE_PAGE_SIZE);
              setActiveAutocompleteQuery(autocompleteQuery);
            }
          }
        } catch (err) {
          console.error("Cégjelző search error:", err);
          if (isMounted.current) {
            setAutocompleteResults([]);
            setOptionsOpen(false);
            setError(i18n.t("searchflow.searcherror"));
            resetAutocompletePagination();
          }
        } finally {
          if (
            isMounted.current &&
            latestSearchQueryRef.current === autocompleteQuery
          ) {
            setLoading(false);
          }
        }
      }, 600),
    [resetAutocompletePagination],
  );

  const inputQualifiesForSearch = useMemo(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return false;
    if (isTaxNumberLikeQuery(trimmed)) {
      return !shouldShowTaxMinDigitsHint(trimmed);
    }
    return trimmed.length >= 3;
  }, [inputValue]);

  useEffect(() => {
    const trimmed = inputValue.trim();
    latestSearchQueryRef.current = isTaxNumberLikeQuery(trimmed)
      ? trimmed
      : normalizeCegjelzoNameSearchQuery(trimmed);

    if (inputQualifiesForSearch) {
      setLoading(true);
      setError(null);
    } else {
      setLoading(false);
    }

    debouncedSearch(inputValue);
    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, debouncedSearch, inputQualifiesForSearch]);

  const loadMoreAutocompleteResults = useCallback(async () => {
    if (loadingMore || !hasMoreResults || !activeAutocompleteQuery) return;
    const currentAutocompleteQuery = isTaxNumberLikeQuery(inputValue)
      ? inputValue.trim()
      : normalizeCegjelzoNameSearchQuery(inputValue);
    if (activeAutocompleteQuery !== currentAutocompleteQuery) return;

    const scrollTop = listboxRef.current?.scrollTop ?? 0;
    listboxScrollLockRef.current = { active: true, scrollTop };
    if (listboxRef.current) {
      listboxRef.current.scrollTop = scrollTop;
    }
    setLoadingMore(true);
    try {
      const nextPage = autocompletePage + 1;
      const response = await cegjelzoApi.autocomplete(activeAutocompleteQuery, {
        page: nextPage,
        limit: CEGJELZO_AUTOCOMPLETE_PAGE_SIZE,
      });
      const nextBatch = response.result;
      if (isMounted.current) {
        setAutocompleteResults((prev) => {
          const seen = new Set(
            prev.map((row) => `${row.full_tax_number}-${row.id}`),
          );
          const merged = [...prev];
          for (const row of nextBatch) {
            const key = `${row.full_tax_number}-${row.id}`;
            if (!seen.has(key)) {
              seen.add(key);
              merged.push(row);
            }
          }
          return merged;
        });
        setAutocompletePage(nextPage);
        setHasMoreResults(nextBatch.length >= CEGJELZO_AUTOCOMPLETE_PAGE_SIZE);
        setOptionsOpen(true);
      }
    } catch (err) {
      console.error("Cégjelző load-more error:", err);
      if (isMounted.current) {
        setHasMoreResults(false);
      }
    } finally {
      if (isMounted.current) setLoadingMore(false);
    }
  }, [
    activeAutocompleteQuery,
    autocompletePage,
    hasMoreResults,
    inputValue,
    loadingMore,
  ]);

  const handleShowMoreClick = useCallback(() => {
    if (loadingMore) return;
    showMoreClickRef.current = true;
    void loadMoreAutocompleteResults();
  }, [loadMoreAutocompleteResults, loadingMore]);

  const optionsWithShowMore = useMemo(() => {
    if (!hasMoreResults || taxNumberLikeInput || autocompleteResults.length === 0) {
      return autocompleteResults;
    }
    return [...autocompleteResults, buildShowMoreOption()];
  }, [autocompleteResults, hasMoreResults, taxNumberLikeInput]);

  const showTaxMinDigitsHint = shouldShowTaxMinDigitsHint(inputValue);
  const showCenteredProgress = loading || fetchingDetails;

  const shouldShowDropdown = useMemo(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return false;
    if (showTaxMinDigitsHint) return false;
    if (!taxNumberLikeInput && trimmed.length < 3) return false;
    if (autocompleteResults.length > 0) return true;
    return false;
  }, [
    inputValue,
    showTaxMinDigitsHint,
    taxNumberLikeInput,
    autocompleteResults.length,
  ]);

  const searchHelperMessage = useMemo(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || showCenteredProgress) {
      return null;
    }

    if (showTaxMinDigitsHint) {
      return {
        text: i18n.t("searchflow.taxmindigitshint"),
      };
    }

    if (!taxNumberLikeInput && trimmed.length < 3) {
      return {
        text: i18n.t("searchflow.namemincharhint"),
      };
    }

    if (
      hasSearched &&
      !loading &&
      autocompleteResults.length === 0 &&
      inputQualifiesForSearch
    ) {
      return {
        title: i18n.t("oneClick.phoneNoResults"),
        text: i18n.t("searchflow.noresultstext"),
      };
    }

    return null;
  }, [
    autocompleteResults.length,
    hasSearched,
    inputQualifiesForSearch,
    inputValue,
    loading,
    showCenteredProgress,
    showTaxMinDigitsHint,
    taxNumberLikeInput,
  ]);

  const handleSelect = async (option) => {
    if (!option || isShowMoreOption(option)) return;
    setSelectedOption(option);

    const taxNumber = option.full_tax_number || option.tax_number;
    if (!taxNumber) {
      const normalizedCompany = normalizeCompanyType(option);
      onCompanySelected(normalizedCompany);
      return;
    }

    setFetchingDetails(true);
    setOptionsOpen(false);
    setError(null);
    try {
      const fullData = await cegjelzoApi.fetchCompanyByTaxNumber(taxNumber);
      const normalizedCompany = normalizeCompanyType(fullData || option);
      if (isMounted.current) {
        onCompanySelected(normalizedCompany);
      }
    } catch (err) {
      console.error("Cégjelző search error:", err);
      if (isMounted.current) {
        setError(i18n.t("searchflow.fetchcompanyerror"));
      }
    } finally {
      if (isMounted.current) {
        setFetchingDetails(false);
      }
    }
  };

  const noOptionsText = useMemo(() => {
    if (showTaxMinDigitsHint) {
      return <small>{i18n.t("searchflow.taxmindigitshint")}</small>;
    }
    if (
      inputValue.trim().length > 0 &&
      inputValue.trim().length < 3 &&
      !taxNumberLikeInput
    ) {
      return <small>{i18n.t("searchflow.namemincharhint")}</small>;
    }
    if (hasSearched && autocompleteResults.length === 0) {
      return (
        <div className={classes.searchHelper}>
          <p className={classes.searchHelperTitle}>{i18n.t("oneClick.phoneNoResults")}</p>
          <p className={classes.searchHelperText}>
            {i18n.t("searchflow.noresultstext")}
          </p>
        </div>
      );
    }
    return "";
  }, [
    inputValue,
    hasSearched,
    autocompleteResults,
    showTaxMinDigitsHint,
    taxNumberLikeInput,
  ]);

  return (
    <div className={classes.root}>
      <div className={classes.searchTop}>
        <div className={classes.searchFieldWrap}>
          <p className={classes.fieldLabel}>{i18n.t("searchflow.cegjelzofieldlabel")}</p>
          <Autocomplete
        className={classes.searchField}
        autoHighlight={false}
        freeSolo
        forcePopupIcon={false}
        options={optionsWithShowMore}
        value={selectedOption}
        inputValue={inputValue}
        disabled={fetchingDetails}
        getOptionLabel={(option) => option.name || option.full_tax_number || ""}
        isOptionEqualToValue={(opt, val) =>
          opt.full_tax_number === val.full_tax_number && opt.id === val.id
        }
        filterOptions={(x) => x}
        open={optionsOpen && shouldShowDropdown && !showCenteredProgress}
        onOpen={() => {
          if (!showCenteredProgress && shouldShowDropdown) {
            setOptionsOpen(true);
          }
        }}
        onClose={(_event, reason) => {
          if (showMoreClickRef.current && reason === "selectOption") {
            showMoreClickRef.current = false;
            return;
          }
          setOptionsOpen(false);
        }}
        loading={false}
        noOptionsText={noOptionsText}
        renderOption={(props, option) => {
          if (isShowMoreOption(option)) {
            const { style: optionStyle, ...optionLiProps } = props;
            return (
              <li
                {...optionLiProps}
                key="cegjelzo-show-more"
                className={`${classes.showMoreRow} ${loadingMore ? classes.showMoreRowLoading : ""}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={(event) => {
                  if (loadingMore) return;
                  event.preventDefault();
                  event.stopPropagation();
                  handleShowMoreClick();
                }}
                style={{
                  ...optionStyle,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {loadingMore ? (
                  <span className={classes.showMoreLoading}>
                    <CircularProgress size={16} sx={{ color: "#1c32f3" }} />
                    <span className={classes.showMoreText}>{i18n.t("searchflow.loadingmoretext")}</span>
                  </span>
                ) : (
                  <span className={classes.showMoreText}>{i18n.t("searchflow.showmoretext")}</span>
                )}
              </li>
            );
          }
          return (
            <li
              {...props}
              key={`${option.full_tax_number ?? option.name}-${option.id ?? ""}`}
            >
              <div>
                <p className={classes.optionName}>{option.name}</p>
                {option.full_tax_number && (
                  <p className={classes.optionMeta}>
                    {i18n.t("general.taxnumber")}: {option.full_tax_number}
                  </p>
                )}
                {option.address && (
                  <p className={classes.optionMeta}>{option.address}</p>
                )}
              </div>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={i18n.t("searchflow.searchplaceholder")}
            variant="outlined"
            fullWidth
            onFocus={() => {
              setIsFocused(true);
              onSearchFocusChange?.(true);
            }}
            onBlur={() => {
              setIsFocused(false);
              onSearchFocusChange?.(false);
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon aria-hidden="true" />
                </InputAdornment>
              ),
            }}
          />
        )}
        slotProps={{
          paper: {
            elevation: 0,
            className: classes.dropdownPaper,
          },
          listbox: {
            ref: listboxRef,
            className: classes.dropdownListbox,
          },
        }}
        onChange={(_event, newValue) => {
          if (isShowMoreOption(newValue)) return;
          if (typeof newValue === "string") return;
          if (newValue) {
            handleSelect(newValue);
          } else {
            setSelectedOption(null);
          }
        }}
        onInputChange={(_event, newInputValue, reason) => {
          if (reason === "clear") {
            latestSearchQueryRef.current = "";
            setInputValue("");
            setSelectedOption(null);
            setAutocompleteResults([]);
            setHasSearched(false);
            setOptionsOpen(false);
            setError(null);
            setLoading(false);
            resetAutocompletePagination();
            return;
          }
          if (reason === "input") {
            setInputValue(newInputValue);
            const trimmed = newInputValue.trim();
            if (
              trimmed.length === 0 ||
              shouldShowTaxMinDigitsHint(newInputValue) ||
              (!isTaxNumberLikeQuery(newInputValue) && trimmed.length < 3)
            ) {
              setOptionsOpen(false);
            }
          }
        }}
        />

        <p className={classes.fieldHint}>
          {i18n.t("searchflow.searchfieldhint")}
        </p>

        {searchHelperMessage && (
          <div className={classes.searchHelper}>
            {searchHelperMessage.title ? (
              <p className={classes.searchHelperTitle}>{searchHelperMessage.title}</p>
            ) : null}
            <p className={classes.searchHelperText}>{searchHelperMessage.text}</p>
          </div>
        )}
        </div>

        {showCenteredProgress && (
          <div className={classes.progressWrap}>
            <CircularProgress size={40} sx={{ color: "#1c32f3" }} />
          </div>
        )}

        {error && <p className={classes.error}>{error}</p>}
      </div>

      <CegjelzoSecurityNotice hidden={isFocused} />
    </div>
  );
};

export default CegjelzoSearch;
